import { Box, Grid, Typography } from '@atgfr8/atg-ui-tailwind';
import { Button, Notification, ToastType } from '@components';
import CommonFileUpload from '@components/FileUpload/CommonFileUpload';
import { ProductFormSkeleton } from '@components/Product/components';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { useParams } from '@solidjs/router';
import {
  addressBookStore,
  downloadSampleCustomerAddressFile,
  setAddressBookStore,
  uploadCustomerAddressFile,
} from '@store/customers/customerDetails/addressBookTab';
import { handleToast, printLog } from '@utils/utils';
import { createSignal, onMount, Show } from 'solid-js';

import { addOrEditAddressStyle as classes } from './classes';
import { addressBookSchema } from './validation';

type InitialAddressFormValues = {
  file: File | string;
  customerId: number;
};

type ImportAddressBookProps = {
  modalId?: string;
  customerId: number;
  onUploadComplete: () => void;
  setIsUploading: (value: boolean) => void;
};

const ImportAddressBook = (props: ImportAddressBookProps) => {
  const params = useParams();
  const [downloadFile, setDownloadFile] = createSignal(false);
  const [isLoading, setIsLoading] = createSignal(false);
  const [failedCsv, setFailedCsv] = createSignal<string | null>(null);

  const { form, isSubmitting, setIsSubmitting, reset } =
    createForm<InitialAddressFormValues>({
      initialValues: {
        file: '',
        customerId: Boolean(props.customerId)
          ? Number(props.customerId)
          : Number(params.id),
      },
      extend: validator({ schema: addressBookSchema }),
      onSubmit: () => {
        (message: string) => {
          handleToast(ToastType.Success, message);
          reset();
          setAddressBookStore('importAddressErrorMessage', '');
        };
        setIsSubmitting(true);
      },
      onError: (errors) => {
        printLog(errors);
      },
    });

  onMount(() => {
    setIsLoading(false);
  });

  const customValidation = (file: File) => {
    if (file.type !== 'text/csv') {
      handleToast(ToastType.Error, 'Only csv files are allowed');
      return false;
    }
    return true;
  };

  const startUpload = async (file: File) => {
    try {
      setIsSubmitting(true);
      props.setIsUploading(true);

      const res = await uploadCustomerAddressFile(props.customerId, file);

      if (!res.isSuccess) {
        handleToast(
          ToastType.Error,
          'Upload failed. Make sure your file is valid.',
        );
        return;
      }

      const { failedCount, failedCsv } = res.value;

      if (failedCount === 0) {
        handleToast(ToastType.Success, 'Addresses uploaded successfully');
        props.onUploadComplete();
        return;
      }

      handleToast(
        ToastType.Caution,
        'Not all addresses were uploaded. Some rows failed validation.',
      );

      setFailedCsv(failedCsv);
      props.onUploadComplete();
    } catch (err) {
      handleToast(ToastType.Error, 'Uploading customer addresses failed');
    } finally {
      setIsSubmitting(false);
      props.setIsUploading(false);
    }
  };

  const downloadCsv = (base64String: string, fileName: string) => {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'text/csv' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleFilePick = (files: FileList | undefined) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!customValidation(file)) return;

    void startUpload(file);
  };

  const handleSingleFilePick = async (file: File): Promise<void> => {
    if (!customValidation(file)) return;

    await startUpload(file);
  };

  return (
    <form ref={form} class={classes.rowFormGrid}>
      <Show when={!isLoading()} fallback={<ProductFormSkeleton />}>
        <Box style={{ 'flex-grow': 1 }}>
          <Show when={addressBookStore.importAddressErrorMessage}>
            <Grid container item size={12} spacing={2} class={classes.errorBox}>
              <Grid item size={12}>
                <Notification
                  type="error"
                  text={addressBookStore.importAddressErrorMessage}
                />
              </Grid>
            </Grid>
          </Show>
          <Grid container spacing={2}>
            <Grid item size={12}>
              <Typography variant="body1">
                From here you can bulk import your address book. The file must
                be a <strong>.csv</strong>.
              </Typography>
            </Grid>
            <Grid
              item
              size={12}
              gap={2}
              display="flex"
              alignItems="center"
              justifyContent="end"
            >
              <Button
                isLoading={downloadFile() && isSubmitting()}
                label="Download Sample File"
                variant="contained"
                type="submit"
                onClick={async () => {
                  setDownloadFile(true);
                  const csv = await downloadSampleCustomerAddressFile();
                  downloadCsv(csv, 'sample_address_book.csv');
                  setDownloadFile(false);
                }}
                sx={{ background: '#468DB5', color: '#FFF' }}
                disabled={isSubmitting()}
                dataTestId="import-address-download-sample-file-btn"
              />
              <Show when={failedCsv()}>
                <Button
                  label="Download Failed Addresses"
                  variant="contained"
                  onClick={() =>
                    downloadCsv(failedCsv()!, 'failed_addresses.csv')
                  }
                  sx={{ background: '#B54646', color: '#FFF' }}
                  disabled={isSubmitting()}
                  dataTestId="import-address-download-failed-addresses-btn"
                />
              </Show>
              <CommonFileUpload
                height={1}
                isMultipleUpload={false}
                maxFileSize={12 * 1024 * 1024}
                shouldUploadOnSelect={true}
                isRemoveFile={false}
                customValidation={customValidation}
                handleFullUploadCustom={handleFilePick}
                acceptedFileTypes={['csv']}
                fileRestrictionMessage="Only .csv files are allowed"
                apiCallBackFn={handleSingleFilePick}
                disabled={isSubmitting()}
              />
            </Grid>
          </Grid>
        </Box>
      </Show>
    </form>
  );
};

export default ImportAddressBook;
